/**
 * Profit Model section (Jeremy's 5 levers) — rendered INSIDE the Exec page as
 * its own section (founder 2026-06-15: "just put it in exec as its own
 * section"). A window-scoped snapshot of Contribution Margin · Acquisition
 * Economics · Lifetime Value · Operational Efficiency · Capital Return Velocity,
 * with the Profit Inflection Point hero and the per-customer payback curve.
 * Click a lever → its trend over time.
 *
 * A lever whose inputs are fully missing shows NO invented number and NO SAMPLE
 * badge (founder 2026-07-13, extending his no-pretend-numbers ruling to the five
 * lever tiles: "putting 'sample' on something gives absolutely no benefit — they
 * need to know WHY the information is not complete"). Instead the tile keeps its
 * title + anchor and its body renders the unified need warning (the same
 * NeedWarning the gated KPI cards use) — naming what's missing and linking the
 * exact fix. Partially-costed levers keep their existing amber partial treatment
 * (CmGapCompact); a lever whose inputs are all entered but that hasn't earned a
 * value in this window yet (e.g. CRV break-even lands beyond it) says "widen the
 * range", never a badge. See the "jeremy-brain" + "profit-model-cockpit" memories.
 */

import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { useApiClient } from "../lib/api-context.js";
import { useSetupGate } from "../lib/setup-gate.js";
import { usePeriod } from "../lib/period.js";
import { Card, Explain, moneyFmt } from "../components/cards.js";
import { TrendChart } from "../components/TrendChart.js";
import { CapitalReturnVelocityCard } from "../components/CapitalReturnVelocityCard.js";
import { CmGapCompact, CmGapNotice } from "../components/CmGapNotice.js";
import { LeverNeedWarning } from "../components/LeverNeedWarning.js";
import type { CmGap } from "../lib/card-requirements.js";
import { usePageFilters } from "../lib/page-filters.js";
import { useCardNameOverride } from "../lib/card-approval.js";
import { CardAnchorCtx } from "../lib/card-anchor.js";
import { DataTrustCardNotice } from "../lib/data-trust-banner.js";
import { OverheadHistoryNotice } from "../components/OverheadHistoryNotice.js";
import { profitTrendValue, type ProfitTrendKey } from "../lib/profit-trends.js";
import { inventoryTimingLabel } from "../lib/cash-timing-format.js";

interface LeverField { value: number | null; prior: number | null; status: "live" | "needs_setup"; needs?: string; sample?: number }
interface ProfitModel {
  periodLabel: string;
  windowDays: number;
  /** Server Capabilities passed through whole; only what this page reads is
   *  typed. cmGap set = PARTIAL costing → every lever is CM-derived here, so
   *  the tiles show the warning instead of numbers (founder 2026-07-11). */
  capabilities: { cmGap?: CmGap | null };
  contributionMargin: LeverField;
  acquisition: { dayOneCmRoas: LeverField; cac: number | null; allowableCac: number | null; headroomPct: number | null; cmAmer: number | null; status: string; needs?: string; sampleHeadroomPct?: number; coverageNote: string | null };
  lifetimeValue: LeverField;
  ltvCacCac: number | null;
  overheadPerCustomer: LeverField;
  operationalDrag: LeverField;
  capitalReturnVelocity: { months: LeverField; inventoryDays: number | null; settlementDays: number | null; paybackDays: number | null };
  profitInflectionPoint: { revenue: LeverField; monthlyRevenue: number; pctAbove: number | null; profitDaysPerMonth: number | null; status: string; needs?: string };
  paybackCurve: Array<{ day: number; cumCmPerCustomer: number; net: number | null }>;
  paybackBreakEvenDay: number | null;
  /** Server verdict: when set, the payback bar is unmeasurable in this window
   *  (no full month / window before spend coverage) — show the honest state,
   *  never the sample curve. */
  paybackUnmeasurableReason: "needs_full_month" | "before_spend_coverage" | null;
  /** Whose margin the payback line averages (server-disclosed basis). */
  paybackCohortNote: string;
  /** CM-LTV markers on the LTV-page fixed-population basis. Feeds the LTV:CAC
   *  drill so it matches the tile, not a different customer population. */
  cmLtvCurve: Array<{ day: number; value: number; cohortSize: number }> | null;
  monthlyOverhead: number | null;
  overheadHistory: {
    recordedMonths: number;
    sparse: boolean;
    trust: "none" | "manual" | "accounting_pending" | "accounting_approved";
    fallbackMonths: number;
  };
  acquisitionCoverageStart: string | null;
  monthly: Array<{ key: string; incomplete: boolean; revenue: number; cm: number; orders: number; customers: number; newCustomers: number; dayOneCm: number; firstOrderCommissions: number; unallocatedCommissions: number; commissions: number; adSpend: number; overhead: number }>;
}

const pctFmt = (v: number | null | undefined): string => (v === null || v === undefined ? "—" : `${Math.round(v * 100)}%`);
const monthsFmt = (v: number | null | undefined): string => (v === null || v === undefined ? "—" : `${v.toFixed(1)} mo`);
const ratioFmt = (v: number | null | undefined): string => (v === null || v === undefined ? "—" : `${v.toFixed(1)}:1`);
const multFmt = (v: number | null | undefined): string => (v === null || v === undefined ? "—" : `${v.toFixed(1)}×`);


type LeverKey = "cm" | "acq" | "ltvcac" | "ops" | "crv" | "pip";

export function ProfitModelSection(): React.ReactElement | null {
  const api = useApiClient();
  const gate = useSetupGate();
  const period = usePeriod();
  const pf = usePageFilters();
  const ready = gate.data?.previewAllowed === true && gate.data.syncStatus === "ready";
  const [open, setOpen] = useState<LeverKey | null>(null);

  const { data } = useQuery({
    queryKey: ["profit-model", period.cacheKey],
    queryFn: () => api.get<ProfitModel>("/dashboard/profit-model", { ...period.query }),
    enabled: ready,
  });

  if (!data) return null;

  const crv = data.capitalReturnVelocity;
  const pip = data.profitInflectionPoint;
  // PARTIAL costing: every lever in this cockpit is contribution-margin-derived
  // (CM%, CM-aMER, CM-LTV:CAC, overhead÷CM, CM payback, break-even÷CM%), so a
  // gap makes ALL of them unknowable — each tile shows the warning instead of a
  // number (founder 2026-07-11; the "Fix it" click opens the costs chat).
  const cmGap = data.capabilities.cmGap && data.capabilities.cmGap.missingProducts > 0 ? data.capabilities.cmGap : null;

  // Lever 2 headline = CM-aMER (founder 2026-06-24): new-customer contribution
  // margin per $1 of marketing spend, as a ×-multiple. Above 1× = acquisition
  // more than pays for itself in margin; higher = better. When CM + marketing
  // spend haven't both landed (backend sends cmAmer = null) the tile shows the
  // need warning. The gap is CM-aMER's OWN missing input (costs first, then
  // spend) — the dayOneCmRoas.needs signal — NOT the acquisition object's
  // overhead-driven allowable-CAC need, which would point at the wrong fix.
  const acq = data.acquisition;
  const acqField: LeverField = acq.cmAmer !== null
    ? { value: acq.cmAmer, prior: null, status: "live" }
    : { value: null, prior: null, status: "needs_setup", needs: acq.dayOneCmRoas.needs };

  // Lifetime value : CAC. The server returns CAC for the exact mature customer
  // group behind the LTV marker, not everyone in the wider selected window.
  const ltvVal = data.lifetimeValue.value;
  const ltvCacRatio = ltvVal !== null && data.ltvCacCac !== null && data.ltvCacCac > 0 ? ltvVal / data.ltvCacCac : null;
  const ltvCacField: LeverField = ltvCacRatio !== null
    ? { value: ltvCacRatio, prior: null, status: "live" }
    : { value: null, prior: null, status: "needs_setup", needs: "contributionMargin" };
  const ltvCacSub = ltvCacRatio !== null && ltvVal !== null && data.ltvCacCac !== null
    ? `${moneyFmt(ltvVal)} CM-LTV vs ${moneyFmt(data.ltvCacCac)} CAC · ${data.paybackCohortNote}`
    : undefined;

  // Operational EFFICIENCY = the share of contribution margin you KEEP after fixed
  // overhead = 1 − operational drag (founder 2026-07-06: "efficiency should be the
  // inverse of drag — 21% drag → ~79% efficiency, I like that better"). Higher is
  // better, so no goodIsDown; the drag figure lives in the tooltip + example.
  const drag = data.operationalDrag;
  const inv = (v: number | null): number | null => (v === null ? null : 1 - v);
  const opsEfficiency: LeverField = {
    value: inv(drag.value),
    prior: inv(drag.prior),
    status: drag.status,
    needs: drag.needs,
    sample: drag.sample === undefined ? undefined : 1 - drag.sample,
  };

  return (
    <section style={{ marginTop: 26, borderTop: "1px solid var(--border)", paddingTop: 18 }}>
      <div style={{ display: "flex", alignItems: "baseline", gap: 12, flexWrap: "wrap", marginBottom: 14 }}>
        <h2 style={{ margin: 0, fontSize: 19 }}>Profit Model</h2>
        <span style={{ marginLeft: "auto", fontSize: 11.5, color: "var(--text-faint)" }}>snapshot · {data.periodLabel}</span>
      </div>
      {/* Only the Contribution-margin lever follows the product rail (real per-SKU
          CM); the other four levers + the Profit Inflection Point are built from
          account-level spend & overhead, which aren't tracked per product — so they
          stay blended under any rail filter. One section note beats repeating it. */}
      {pf.filterActive && (
        <div style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 14, padding: "7px 11px", borderRadius: 8, background: "var(--surface-2)", border: "1px solid var(--border)", color: "var(--body-faint)", fontSize: 11.5, lineHeight: 1.4 }}>
          <span aria-hidden="true" style={{ fontSize: 13 }}>🌐</span>
          Only Contribution margin follows the product filter here — the other levers stay Blended (marketing spend &amp; overhead aren&rsquo;t tracked per product, so the filter can&rsquo;t move them).
        </div>
      )}

      {/* 5 levers + the Profit Inflection Point hero in a 3×2 grid (founder
          2026-06-16: "3 columns, 2 rows" — was 5-in-a-row + PIP on its own). */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: 12 }}>
        <LeverTile
          k="cm" open={open} setOpen={setOpen} cmGap={cmGap} anchor="pm-contribution-margin"
          title="Contribution margin" timeframe={data.periodLabel}
          field={data.contributionMargin} fmt={pctFmt}
          health={cmHealth(data.contributionMargin.value)}
          explain={["<b>What it is:</b> What you keep from each sale after ALL variable costs (product cost, fulfillment, payment fees, refunds) — not gross margin.", "<b>How it's calculated:</b> Contribution margin dollars ÷ revenue, for the selected window.", "<b>Example (sample data · year-to-date):</b> $611,997 margin on the period's revenue = 55% CM.", "<b>Note:</b> Blended — a company-wide average across every order, the same as the rest of the profit model, so the product filter doesn't move it. It always uses each item's real entered costs, never revenue × an average margin %. To see one product's own margin, use the Products page. The single most powerful lever — a 1-point gain in CM is worth roughly 4–6% more net profit, and it lifts every other lever. Click for CM% by month."]}
        />
        <LeverTile
          k="acq" open={open} setOpen={setOpen} cmGap={cmGap} anchor="pm-acquisition-economics"
          title="Acquisition economics" timeframe={data.periodLabel}
          field={acqField} fmt={multFmt}
          health={cmAmerHealth(acq.cmAmer)}
          sub={acq.coverageNote ?? undefined}
          trustWarning={<DataTrustCardNotice card="aMER" />}
          explain={["<b>What it is:</b> CM-aMER is the contribution margin from a new customer's first order for every $1 spent to acquire them. It answers one question: did the first purchase pay back acquisition?", "<b>How it's calculated:</b> First-order contribution margin from new customers ÷ acquisition spend from those same dates. Repeat purchases and commissions on repeat orders stay out.", "<b>Example (sample data · year-to-date):</b> 0.97× means each $1 spent brought back 97¢ of first-order contribution margin.", "<b>Note:</b> Blended, so a product filter will not move it. If the selected dates reach before acquisition costs were tracked, customers, margin, and spend all start at the first covered month. The payoff from reorders appears in Lifetime Value to Customer Acquisition Cost."]}
        />
        <LeverTile
          k="ltvcac" open={open} setOpen={setOpen} cmGap={cmGap} anchor="pm-ltv-cac"
          title="Lifetime value : CAC" timeframe={data.periodLabel}
          field={ltvCacField} fmt={ratioFmt}
          health={ltvCacHealth(ltvCacRatio)}
          sub={ltvCacSub}
          trustWarning={<DataTrustCardNotice card="LTV:CAC" />}
          footerLink={{ label: "View by channel →", to: "/cohorts?tab=source" }}
          explain={["<b>What it is:</b> A fully matured customer group's Contribution Margin Lifetime Value compared with what that exact group cost to acquire. 3:1 or better is healthy.", "<b>How it's calculated:</b> The picker chooses the horizon. A 90-day view uses the most recent customers who have all completed 90 days. Their average 90-day Contribution Margin LTV is divided by the cost to acquire those exact people.", "<b>Example:</b> $49.01 of 90-day Contribution Margin LTV divided by $18.44 to acquire those same customers = 2.66:1.", "<b>Note:</b> This CAC can differ from the Acquisition tile. The Acquisition tile follows the literal dates you picked. LTV:CAC uses the most recent fully completed cohort for that horizon. The exact customer group appears under the number. Missing acquisition spend never counts as $0."]}
        />
        <LeverTile
          k="ops" open={open} setOpen={setOpen} cmGap={cmGap} anchor="pm-operational-efficiency"
          title="Operational efficiency" timeframe={data.periodLabel}
          field={opsEfficiency} fmt={pctFmt}
          health={opsEfficiencyHealth(opsEfficiency.value)}
          trustWarning={<OverheadHistoryNotice history={data.overheadHistory} />}
          explain={["<b>What it is:</b> The share of your contribution margin you KEEP after fixed overhead — the flip side of &ldquo;operational drag.&rdquo; Higher means your operations scale more profitably.", "<b>How it's calculated:</b> 1 − (overhead ÷ contribution margin) for the period. The complement — overhead ÷ CM — is your operational drag.", "<b>Example (sample data · year-to-date):</b> $132,000 overhead ÷ $611,997 margin = 22% drag, so <b>78% operational efficiency</b>.", "<b>Note:</b> Blended — overhead isn't tracked per product. At 0% overhead eats all your margin (break-even); the higher the better, and it should rise as you grow. Click for the by-month trend."]}
        />
        <LeverTile
          k="crv" open={open} setOpen={setOpen} emphasize cmGap={cmGap} anchor="pm-capital-return-velocity"
          title="Capital return velocity" timeframe={data.periodLabel}
          field={crv.months} fmt={monthsFmt} goodIsDown
          health="neutral"
          trustWarning={<DataTrustCardNotice card="Capital Return Velocity" />}
          activate={scrollToFlagship}
          activateTitle="See the full capital-return-velocity journey"
          explain={["<b>What it is:</b> How long a dollar stays out of your bank account as it moves through inventory, acquisition, overhead, and payout.", "<b>How it's calculated:</b> Your own cash days in inventory + the days for Contribution Margin to recover that completed customer group's acquisition cost and its share of overhead from the same dates + payout lag. Products that tie up more product-cost dollars carry more weight in the inventory time. Product cost is already removed inside Contribution Margin, so inventory is counted as time and never charged twice.", "<b>Example (sample data):</b> 26 days in inventory + 1 day to recover acquisition + 36 more days to recover overhead + 6 days for payout = 69 days, or 2.3 months.", "<b>Note:</b> Blended. The picker chooses the measurement horizon, and the card uses the most recent customer group that has fully completed it. If overhead is unconfirmed or a month needs the planning fallback, the card tells you. If that group has not reached full payback, the full card shows its progress instead of guessing. Click for the full breakdown."]}
        />
        <HeroTile title="Profit inflection point" anchor="pm-profit-inflection-point" timeframe={data.periodLabel} cmGap={cmGap}
          field={pip.revenue} fmt={(v) => `${moneyFmt(v)}/mo`}
          onClick={() => setOpen("pip")} isOpen={open === "pip"}
          trustWarning={<OverheadHistoryNotice history={data.overheadHistory} />}
          explain={["<b>What it is:</b> The monthly revenue you need to cover overhead and your current acquisition spend. Below it the month loses money. Above it, each extra dollar adds profit.", "<b>How it's calculated:</b> Your approved planning overhead plus average monthly acquisition spend from your three most recent complete sales months, divided by the net Contribution Margin rate from those same three months. Revenue has sales tax removed and shipping stays in. If your store gives us one refund total for the whole order, we subtract only the part that belongs to those sales dollars.", "<b>Example (sample data):</b> About $137,428 per month to break even. At about $202,982 in net monthly revenue, that is roughly 48% above break-even.", "<b>Note:</b> Blended. Overhead and acquisition spend are not tracked per product. The profit-days line is only a simple way to picture the cushion. It assumes sales arrive evenly through a 30-day month, so it is not a count of actual profitable calendar days."]}
          sub={pip.revenue.status === "live" && pip.pctAbove !== null
            ? pip.pctAbove >= 0
              ? `break-even at current spend · ${Math.round(pip.pctAbove * 100)}% above · about ${pip.profitDaysPerMonth ?? 0} profit days if sales were even`
              : `break-even at current spend · ${Math.round(Math.abs(pip.pctAbove) * 100)}% below · this month loses money at the current pace`
            : "break-even revenue at current spend"} />
      </div>

      {/* The clicked lever's trend renders right under the grid — instant,
          visible feedback (founder 2026-06-16: "most don't open anything when
          clicked"; it used to mount below the payback curve, off-screen). */}
      {open && <DetailPanel k={open} data={data} cmGap={cmGap} onClose={() => setOpen(null)} />}

      {/* THE flagship Capital-Return-Velocity card — the layered payback journey
          with the CRV-months headline + prominent break-even %. It renders the
          same component (and rides the same shared window) as the Financials
          placement, absorbing the old Exec payback curve. The compact CRV tile
          above scrolls here. */}
      <div style={{ marginTop: 16 }}>
        <CapitalReturnVelocityCard anchor="pm-payback-curve" />
      </div>
    </section>
  );
}

/** Smooth-scroll to the flagship card (the CRV number tile's click target). */
function scrollToFlagship(): void {
  document.querySelector('[data-card-anchor="pm-payback-curve"]')?.scrollIntoView({ behavior: "smooth", block: "start" });
}

function cmHealth(v: number | null): Health { return v === null ? "neutral" : v >= 0.5 ? "good" : v >= 0.4 ? "warn" : "bad"; }
// CM-aMER: new-customer CM per $1 of marketing spend; higher = better. Above 1×
// acquisition pays for itself in margin; 0.5–1× watch; under 0.5× thin.
function cmAmerHealth(v: number | null): Health { return v === null ? "neutral" : v >= 1 ? "good" : v >= 0.5 ? "warn" : "bad"; }
// CM-LTV : CAC — higher is better; 3:1+ healthy, 1.5–3 watch, under 1.5 thin.
function ltvCacHealth(v: number | null): Health { return v === null ? "neutral" : v >= 3 ? "good" : v >= 1.5 ? "warn" : "bad"; }
// Operational efficiency (1 − drag) — higher is better; ≥70% keep (drag <30%)
// healthy, 50–70% watch, under 50% (overhead eats over half the margin) thin.
function opsEfficiencyHealth(v: number | null): Health { return v === null ? "neutral" : v >= 0.7 ? "good" : v >= 0.5 ? "warn" : "bad"; }

type Health = "good" | "warn" | "bad" | "neutral";
const HEALTH_DOT: Record<Health, string> = { good: "var(--good)", warn: "#EF9F27", bad: "var(--bad)", neutral: "var(--accent)" };

function LeverTile({ k, open, setOpen, title, anchor, timeframe, field, fmt, health, explain, goodIsDown, emphasize, sub, cmGap, activate, activateTitle, footerLink, trustWarning }: {
  k: LeverKey;
  open: LeverKey | null;
  setOpen: (k: LeverKey) => void;
  title: string;
  /** The catalog renderId — "See it live →" anchor + rename-override key. */
  anchor?: string;
  /** Small muted timeframe label after the title (founder 2026-06-22). */
  timeframe?: string;
  field: LeverField;
  fmt: (v: number | null | undefined) => string;
  health: Health;
  explain: string[];
  goodIsDown?: boolean;
  emphasize?: boolean;
  /** One muted line of essential context under the number (e.g. acquisition's
   *  "$29 of $100 allowable"); shown only when the lever is live. */
  sub?: string;
  /** PARTIAL costing → the warning replaces the number (founder 2026-07-11). */
  cmGap?: CmGap | null;
  /** Overrides the default click (open this lever's detail panel). The CRV tile
   *  uses it to SCROLL to the flagship Capital-Return-Velocity card instead. */
  activate?: () => void;
  activateTitle?: string;
  /** A small "→" link at the bottom of the tile (stops propagation). Sends a blended
   *  lever to where it CAN be split — the LTV lever's "View by channel →" → the
   *  Customer LTV page's Source tab (founder's blended model 2026-07-13). */
  footerLink?: { label: string; to: string };
  /** Known source-history gap that can affect this otherwise-valid result. */
  trustWarning?: React.ReactNode;
}): React.ReactElement {
  const live = field.status === "live" && field.value !== null;
  // Fully missing inputs → the amber need warning (below), never a sample number.
  const needsSetup = field.status === "needs_setup";
  const isOpen = open === k;
  const shownTitle = useCardNameOverride(anchor) ?? title;
  return (
    // Provide THIS tile's own renderId so the ⓘ resolves its catalog copy (and any
    // operator override) by its real id — not the section it's nested in (founder
    // 2026-07-13 single-source; otherwise all six levers resolve to "profit-model").
    <CardAnchorCtx.Provider value={anchor ?? null}>
    <div onClick={() => (activate ? activate() : setOpen(k))} className="dd-clickable-card" data-card-anchor={anchor} title={activateTitle}
      style={{ background: "var(--surface)", border: `1px solid ${isOpen ? "var(--accent)" : emphasize ? "var(--accent-2)" : "var(--border)"}`, borderRadius: 10, padding: "11px 12px", cursor: "pointer" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 6 }}>
        <span style={{ fontSize: 11.5, color: "var(--body)", display: "inline-flex", alignItems: "center", gap: 5, flexWrap: "wrap" }}>
          {shownTitle}
          {timeframe && <span style={{ color: "var(--text-faint)", fontWeight: 400 }}>· {timeframe}</span>}
          <span onClick={(e) => e.stopPropagation()}><Explain title={shownTitle} lines={explain} /></span>
        </span>
        <span style={{ width: 8, height: 8, borderRadius: "50%", background: !cmGap && live ? HEALTH_DOT[health] : "var(--text-faint)", flexShrink: 0 }} />
      </div>
      {cmGap ? (
        // Some products are selling with no costs → this CM-derived lever is
        // unknowable. Amber warning instead of a number — no estimate.
        <CmGapCompact gap={cmGap} />
      ) : needsSetup ? (
        // Inputs fully missing → the unified need warning (names what's missing +
        // one-click fix), never an invented number or a SAMPLE badge (founder
        // 2026-07-13, no-pretend-numbers ruling extended to the lever tiles).
        <LeverNeedWarning needs={field.needs} compact />
      ) : (
        <>
          <div className="dd-num" style={{ fontSize: 22, fontWeight: 700, color: "var(--num)", margin: "4px 0 2px" }}>
            {fmt(field.value)}
          </div>
          {live ? (
            <MiniDelta value={field.value} prior={field.prior} percentPoints={fmt === pctFmt} goodIsDown={goodIsDown} />
          ) : (
            // Inputs all entered but no value in this window yet (e.g. CRV break-even
            // lands beyond it): NOT missing setup — say to widen, never a badge
            // (founder 2026-07-06: "shows sample even though the payback curve is real").
            <span style={{ fontSize: 10, color: "var(--text-faint)", lineHeight: 1.4 }}>not reached in this window yet — widen the range</span>
          )}
          {sub && live && <div style={{ fontSize: 10, color: "var(--text-faint)", marginTop: 3 }}>{sub}</div>}
          {footerLink && (
            <Link to={footerLink.to} onClick={(e) => e.stopPropagation()}
              style={{ display: "inline-block", marginTop: 6, fontSize: 10.5, fontWeight: 600, color: "var(--accent)", textDecoration: "none" }}>
              {footerLink.label}
            </Link>
          )}
        </>
      )}
      {trustWarning}
    </div>
    </CardAnchorCtx.Provider>
  );
}

function MiniDelta({ value, prior, percentPoints, goodIsDown }: { value: number | null; prior: number | null; percentPoints?: boolean; goodIsDown?: boolean }): React.ReactElement {
  if (value === null || prior === null || prior === 0) return <div style={{ fontSize: 10.5, color: "var(--text-faint)" }}>&nbsp;</div>;
  const rawDiff = percentPoints ? (value - prior) * 100 : ((value - prior) / Math.abs(prior)) * 100;
  const up = value >= prior;
  const good = goodIsDown ? !up : up;
  const text = percentPoints ? `${rawDiff >= 0 ? "+" : ""}${rawDiff.toFixed(1)} pts` : `${Math.abs(rawDiff).toFixed(0)}%`;
  return (
    <div style={{ fontSize: 11, color: good ? "var(--good)" : "var(--bad)", display: "flex", alignItems: "center", gap: 3 }}>
      <span>{up ? "▲" : "▼"}</span><span className="dd-num">{text}</span>
    </div>
  );
}

function HeroTile({ title, anchor, timeframe, field, fmt, sub, onClick, isOpen, explain, cmGap, trustWarning }: { title: string; anchor?: string; timeframe?: string; field: LeverField; fmt: (v: number | null | undefined) => string; sub: string; onClick?: () => void; isOpen?: boolean; explain?: string[]; cmGap?: CmGap | null; trustWarning?: React.ReactNode }): React.ReactElement {
  // Not cmGap and not needsSetup → status is "live"; show the value + live sub.
  const needsSetup = field.status === "needs_setup";
  const shownTitle = useCardNameOverride(anchor) ?? title;
  return (
    <CardAnchorCtx.Provider value={anchor ?? null}>
    <div onClick={onClick} className={onClick ? "dd-clickable-card" : undefined} data-card-anchor={anchor}
      style={{ background: "var(--surface)", borderRadius: 10, padding: "12px 14px", border: `1px solid ${isOpen ? "var(--accent)" : "var(--border)"}`, ...(onClick ? { cursor: "pointer" } : {}) }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
        <span style={{ fontSize: 11.5, color: "var(--body)", display: "inline-flex", alignItems: "center", gap: 5, flexWrap: "wrap" }}>{shownTitle}{timeframe && <span style={{ color: "var(--text-faint)", fontWeight: 400 }}>· {timeframe}</span>}{explain && <span onClick={(e) => e.stopPropagation()}><Explain title={shownTitle} lines={explain} /></span>}</span>
      </div>
      {cmGap ? (
        // Break-even ÷ CM% with an unknowable CM% isn't a break-even — warning
        // instead of a number (founder 2026-07-11).
        <CmGapCompact gap={cmGap} />
      ) : needsSetup ? (
        // Inputs fully missing → the unified need warning, never a number or a
        // SAMPLE badge (founder 2026-07-13, no-pretend-numbers ruling).
        <LeverNeedWarning needs={field.needs} compact />
      ) : (
        <>
          <div className="dd-num" style={{ fontSize: 26, fontWeight: 700, color: "var(--num)", margin: "3px 0 2px" }}>{fmt(field.value)}</div>
          <div style={{ fontSize: 11, color: "var(--text-faint)", lineHeight: 1.45 }}>{sub}</div>
        </>
      )}
      {trustWarning}
    </div>
    </CardAnchorCtx.Provider>
  );
}

/** Click-through: a lever's trend over time (or, for CRV, its component breakdown). */
function DetailPanel({ k, data, cmGap, onClose }: { k: LeverKey; data: ProfitModel; cmGap?: CmGap | null; onClose: () => void }): React.ReactElement {
  // The newest synced month may only contain part of the month's data. Keep it
  // out of every by-month comparison so a paused or mid-month import cannot
  // manufacture a downturn.
  const coverageMonth = data.acquisitionCoverageStart?.slice(0, 7) ?? null;
  const spendDependent = k === "acq" || k === "pip";
  const m = data.monthly.filter((row) =>
    !row.incomplete && (!spendDependent || !coverageMonth || row.key >= coverageMonth),
  );
  const title: Record<LeverKey, string> = { cm: "Contribution margin", acq: "CM-aMER (new-customer CM ÷ acquisition spend)", ltvcac: "Lifetime value vs CAC", ops: "Operational efficiency (share of margin kept)", crv: "Capital return velocity", pip: "Profit inflection point" };
  // These month-by-month lever trends deliberately span the account's FULL order
  // history (first order → now), NOT the picked window (founder 2026-06-22) — so
  // a founder on "30 days" who opens a lever still sees the whole multi-year
  // shape. Disclose that on screen so it doesn't read as a picker that's stuck.
  const spansAllHistory = !cmGap && (k === "cm" || k === "acq" || k === "ops" || k === "pip");

  let body: React.ReactNode;
  if (cmGap) {
    // Every lever here is CM-derived, so its trend is just as unknowable as
    // its headline while products are missing costs (founder 2026-07-11).
    body = <CmGapNotice gap={cmGap} />;
  } else if (k === "crv") {
    const crv = data.capitalReturnVelocity;
    if (crv.months.value === null) body = crv.months.needs
      ? <LeverNeedNote needs={crv.months.needs} />
      : <div style={{ fontSize: 12.5, color: "var(--text-faint)", padding: "14px 0", lineHeight: 1.6 }}>Everything&rsquo;s entered — but this window&rsquo;s customers haven&rsquo;t reached FULL break-even inside it yet, so there&rsquo;s no capital-return figure. The payback curve shows how far along they are (% of the way to full break-even); widen the timeline to see a completed round trip.</div>;
    else body = (
      <div style={{ fontSize: 13, color: "var(--body)", lineHeight: 1.7 }}>
        Inventory timing <b className="dd-num" style={{ color: "var(--num)" }}>{inventoryTimingLabel(crv.inventoryDays ?? 0)}</b> + payback (acquisition + overhead) <b className="dd-num" style={{ color: "var(--num)" }}>{Math.round(crv.paybackDays ?? 0)}d</b>{(crv.settlementDays ?? 0) >= 1 ? <> + payout lag <b className="dd-num" style={{ color: "var(--num)" }}>{Math.round(crv.settlementDays!)}d</b></> : null} = <b className="dd-num" style={{ color: "var(--num)" }}>{monthsFmt(crv.months.value)}</b> to recoup each dollar in cash.
      </div>
    );
  } else if (k === "ltvcac") {
    // How lifetime value builds over the customer journey (cumulative CM-LTV per
    // customer by day) — the curve behind the headline ratio. Reads the LTV-PAGE
    // fixed-population basis markers (cmLtvCurve), not an independently built
    // curve that could silently swap the people behind the tile.
    const pts = (data.cmLtvCurve ?? []).map((p) => ({ label: `Day ${p.day}`, a: p.value }));
    body = pts.length >= 2
      ? <TrendChart kind="line" switchKinds={["line", "area", "table"]} chartKey="pm-ltvcac" money showValues valueFmt={(n: number) => moneyFmt(n)} points={pts} />
      : <LeverNeedNote needs="contributionMargin" />;
  } else {
    const hasTrustedOverhead = data.overheadHistory.trust === "manual"
      || data.overheadHistory.trust === "accounting_approved";
    const points = m.map((row) => {
      const a = profitTrendValue(k as ProfitTrendKey, row, hasTrustedOverhead);
      return { label: row.key, a };
    });
    const isPct = k === "cm" || k === "ops";
    const isMult = k === "acq"; // CM-aMER renders as a ×-multiple, not money/%.
    const isMoney = !isPct && !isMult;
    const valueFmt = isPct ? (n: number) => `${Math.round(n)}%` : isMult ? (n: number) => `${n.toFixed(1)}×` : (n: number) => moneyFmt(n);
    const allZero = points.every((p) => p.a === 0);
    body = allZero
      ? <LeverNeedNote needs={k === "cm" || k === "pip" ? "contributionMargin" : k === "ops" ? "overhead" : "marketingSpend"} />
      : <TrendChart kind="line" switchKinds={["line", "area", "step", "table", "number"]} chartKey={`pm-${k}`} money={isMoney} showValues valueFmt={valueFmt} points={points} />;
  }

  return (
    <Card style={{ marginTop: 16 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <span style={{ color: "var(--text)", opacity: 0.85, fontSize: 12.5, fontWeight: 600 }}>{`${title[k]} over time`}</span>
          <Explain title={`${title[k]} over time`} lines={["<b>What it is:</b> How this profit lever has moved month by month — the trend behind the single headline number.", "<b>How it's calculated:</b> The lever's value recomputed for each month across your full order history, plotted in order.", "<b>Example:</b> Contribution margin drifting from 58% up to 61% over six months.", "<b>Note:</b> This trend spans your full history by design, so it does NOT change with the date picker (the headline number above it does). Click the lever tile again to collapse this."]} />
        </div>
        <button onClick={onClose} aria-label="Close" style={{ border: "none", background: "transparent", color: "var(--body-faint)", cursor: "pointer", fontSize: 13 }}>✕</button>
      </div>
      {spansAllHistory && (
        <div style={{ fontSize: 11, color: "var(--body-faint)", marginTop: 2 }}>
          {spendDependent && coverageMonth
            ? `Complete months since ${new Date(`${coverageMonth}-01T00:00:00.000Z`).toLocaleDateString("en-US", { month: "short", year: "numeric", timeZone: "UTC" })}, when marketing cost tracking starts.`
            : "Full history. The headline above still follows the date picker."}
        </div>
      )}
      <div style={{ marginTop: 10 }}>{body}</div>
    </Card>
  );
}

/** The trend click-through when a lever isn't set up: the same unified amber
 *  need warning as the tile — names what's missing + links the fix, never a
 *  SAMPLE badge or a fake trend line. */
function LeverNeedNote({ needs }: { needs?: string }): React.ReactElement {
  return (
    <div style={{ padding: "8px 0" }}>
      <LeverNeedWarning needs={needs} />
    </div>
  );
}
